import React, { useState, useEffect } from 'react';
import Navbar from '../../Shared/AdminSidebar/AdminSidebar';
import '../../Layout/Collapse/Collapse.css';
import './Dashboard.css';
import { baseUrl } from '../../../Apiurls';

const Dashboard = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [attendanceData, setAttendanceData] = useState({
        totalEmployees: 0,
        daily: { present: 0, absent: 0, total: 0 },
        monthly: { present: 0, absent: 0, total: 0 },
        recentActivity: [],
        summary: { dailyPresentRate: 0, monthlyPresentRate: 0, averageDailyAttendance: 0, totalWorkingDays: 30 }
    });
    const [loading, setLoading] = useState(true);

    // Fetch dashboard data
    const fetchDashboardData = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${baseUrl}/api/admin/dashboard-stats`);
            const data = await response.json();
            if (data.success) {
                setAttendanceData(data.data);
            }
        } catch (error) {
            console.error('Error fetching dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    // Check-in function
    const handleCheckIn = async (employeeId) => {
        try {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(async (position) => {
                    const location = `${position.coords.latitude},${position.coords.longitude}`;
                    const response = await fetch(`${baseUrl}/api/admin/checkin`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ employee_id: employeeId, location })
                    });
                    const result = await response.json();
                    if (response.ok) {
                        alert('Checked in successfully!');
                        fetchDashboardData();
                    } else {
                        alert(result.message || 'Check-in failed');
                    }
                }, (error) => {
                    alert('Unable to get location: ' + error.message);
                });
            } else {
                alert('Geolocation is not supported by your browser');
            }
        } catch (error) {
            console.error('Check-in error:', error);
            alert('Error during check-in');
        }
    };

    // Check-out function
    const handleCheckOut = async (employeeId) => {
        try {
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(async (position) => {
                    const location = `${position.coords.latitude},${position.coords.longitude}`;
                    const response = await fetch(`${baseUrl}/api/admin/checkout`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ employee_id: employeeId, location })
                    });
                    const result = await response.json();
                    if (response.ok) {
                        alert('Checked out successfully!');
                        fetchDashboardData();
                    } else {
                        alert(result.message || 'Check-out failed');
                    }
                }, (error) => {
                    alert('Unable to get location: ' + error.message);
                });
            } else {
                alert('Geolocation is not supported by your browser');
            }
        } catch (error) {
            console.error('Check-out error:', error);
            alert('Error during check-out');
        }
    };

    const getEmployeeIdFromActivity = (activity) => activity.employee_id || activity.id;

    useEffect(() => {
        fetchDashboardData();
        const interval = setInterval(fetchDashboardData, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const dailyPresentPercentage = attendanceData.summary.dailyPresentRate;
    const monthlyPresentPercentage = attendanceData.summary.monthlyPresentRate;

    if (loading) {
        return (
            <div className='CollapseContainer'>
                <Navbar onToggleSidebar={setCollapsed} />
                <div className={`Collapse ${collapsed ? 'collapsed' : ''}`}>
                    <div className="dashboard-container">
                        <div className="loading-spinner">
                            <div className="spinner"></div>
                            <p>Loading dashboard data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className='CollapseContainer'>
            <Navbar onToggleSidebar={setCollapsed} />
            <div className={`Collapse ${collapsed ? 'collapsed' : ''}`}>
                <div className="dashboard-container">
                    {/* Header */}
                    <div className="dashboard-header">
                        <h1>Admin Dashboard</h1>
                        <div className="date-display">
                            {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <button className="refresh-btn" onClick={fetchDashboardData}>↻ Refresh Data</button>
                    </div>

                    {/* Stats Cards */}
                    <div className="stats-grid">
                        {/* Daily Attendance */}
                        <div className="stat-card">
                            <div className="stat-header">
                                <h3>Daily Attendance</h3>
                                <span className="stat-date">{new Date().toLocaleDateString()}</span>
                            </div>
                            <div className="stat-content">
                                <div className="stat-main">
                                    <div className="stat-value">{attendanceData.daily.present}</div>
                                    <div className="stat-label">Present Today</div>
                                </div>
                                <div className="stat-details">
                                    <div className="stat-item">
                                        <span className="stat-item-label">Absent:</span>
                                        <span className="stat-item-value">{attendanceData.daily.absent}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-item-label">Total Employees:</span>
                                        <span className="stat-item-value">{attendanceData.daily.total}</span>
                                    </div>
                                </div>
                                <div className="stat-progress">
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${dailyPresentPercentage}%` }}></div>
                                    </div>
                                    <span className="progress-text">{dailyPresentPercentage}% Present</span>
                                </div>
                            </div>
                        </div>

                        {/* Monthly Attendance */}
                        <div className="stat-card">
                            <div className="stat-header">
                                <h3>Monthly Attendance</h3>
                                <span className="stat-date">{new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                            </div>
                            <div className="stat-content">
                                <div className="stat-main">
                                    <div className="stat-value">{attendanceData.monthly.present}</div>
                                    <div className="stat-label">Present This Month</div>
                                </div>
                                <div className="stat-details">
                                    <div className="stat-item">
                                        <span className="stat-item-label">Absent:</span>
                                        <span className="stat-item-value">{attendanceData.monthly.absent}</span>
                                    </div>
                                    <div className="stat-item">
                                        <span className="stat-item-label">Total Employees:</span>
                                        <span className="stat-item-value">{attendanceData.monthly.total}</span>
                                    </div>
                                </div>
                                <div className="stat-progress">
                                    <div className="progress-bar">
                                        <div className="progress-fill" style={{ width: `${monthlyPresentPercentage}%` }}></div>
                                    </div>
                                    <span className="progress-text">{monthlyPresentPercentage}% Present</span>
                                </div>
                            </div>
                        </div>

                        {/* Quick Stats */}
                        <div className="quick-stats-grid">
                            <div className="quick-stat-card present">
                                <div className="quick-stat-icon">✓</div>
                                <div className="quick-stat-content">
                                    <div className="quick-stat-value">{attendanceData.daily.present}</div>
                                    <div className="quick-stat-label">Present Today</div>
                                </div>
                            </div>
                            <div className="quick-stat-card absent">
                                <div className="quick-stat-icon">✗</div>
                                <div className="quick-stat-content">
                                    <div className="quick-stat-value">{attendanceData.daily.absent}</div>
                                    <div className="quick-stat-label">Absent Today</div>
                                </div>
                            </div>
                            <div className="quick-stat-card monthly">
                                <div className="quick-stat-icon">📅</div>
                                <div className="quick-stat-content">
                                    <div className="quick-stat-value">{monthlyPresentPercentage}%</div>
                                    <div className="quick-stat-label">Monthly Rate</div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Recent Activity */}
                    {/* Recent Activity */}
<div className="recent-activity">
    <h2>Recent Attendance Activity</h2>
    <div className="activity-table">
        <table>
            <thead>
                <tr>
                    <th>Employee</th>
                    <th>Check-in Time</th>
                    <th>Status</th>
                    <th>Current Status</th>
                </tr>
            </thead>
            <tbody>
                {attendanceData.recentActivity.length > 0 ? (
                    // Sort by time descending and take only 5
                    attendanceData.recentActivity
                        .sort((a, b) => new Date(b.time) - new Date(a.time))
                        .slice(0, 5)
                        .map(activity => (
                            <tr key={activity.employee_id}>
                                <td>{activity.name}</td>
                                <td>
                                    {activity.time
                                        ? new Date(activity.time).toLocaleString('en-IN', {
                                              day: '2-digit',
                                              month: 'short',
                                              year: 'numeric',
                                              hour: '2-digit',
                                              minute: '2-digit',
                                              hour12: true,
                                          })
                                        : '-'}
                                </td>
                                <td>
                                    <span className={`status-badge ${activity.status?.toLowerCase() || 'absent'}`}>
                                        {activity.status || 'Absent'}
                                    </span>
                                </td>
                                <td>
                                    <span
                                        className={`status-badge ${
                                            activity.currentStatus?.toLowerCase().replace(' ', '-') || 'not-checked-in'
                                        }`}
                                    >
                                        {activity.currentStatus || 'Not Checked In'}
                                    </span>
                                </td>
                            </tr>
                        ))
                ) : (
                    <tr>
                        <td colSpan="4" className="no-data">
                            No attendance data available
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    </div>
</div>


                    {/* Summary Section */}
                    {/* <div className="summary-section">
                        <div className="summary-card">
                            <h3>Attendance Summary</h3>
                            <div className="summary-content">
                                <div className="summary-item">
                                    <span>Daily Present Rate:</span>
                                    <strong>{attendanceData.summary.dailyPresentRate}%</strong>
                                </div>
                                <div className="summary-item">
                                    <span>Monthly Present Rate:</span>
                                    <strong>{attendanceData.summary.monthlyPresentRate}%</strong>
                                </div>
                                <div className="summary-item">
                                    <span>Average Daily Attendance:</span>
                                    <strong>{attendanceData.summary.averageDailyAttendance}</strong>
                                </div>
                              
                            </div>
                        </div>
                    </div> */}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
